import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AuthResponse, SocialAttachEmailRequest, SocialLoginRequest, SocialLoginResponse, UserProfile } from '../models/auth';
import { ApiResponse } from '../models/api-response';

export interface User {
  id: string;
  email: string;
  name?: string;
  avatar?: string;
  hasActiveReaderSubscription?: boolean;
  hasActiveWriterSubscription?: boolean;
  /** When false, user must complete email verification (password registration). */
  emailConfirmed?: boolean;
  /** True when this account is on the Iwan newsletter list. */
  newsletterSubscribed?: boolean;
}

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
/** Session flag: show dismissable welcome banner once after login/register/social sign-in */
const WELCOME_BANNER_SESSION_KEY = 'iwan_show_welcome_banner';

function profileToUser(p: UserProfile): User {
  return {
    id: p.id,
    email: p.email,
    name: p.displayName || [p.firstName, p.lastName].filter(Boolean).join(' ') || undefined,
    avatar: p.avatarUrl || undefined,
    hasActiveReaderSubscription: p.hasActiveReaderSubscription ?? false,
    hasActiveWriterSubscription: p.hasActiveWriterSubscription ?? false,
    emailConfirmed: p.emailConfirmed !== undefined ? p.emailConfirmed : true,
    newsletterSubscribed: p.newsletterSubscribed === true
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(this.getStoredUser());
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private api: ApiService,
    private router: Router
  ) {
    this.initFromStorage();
  }

  private initFromStorage(): void {
    const token = this.getToken();
    if (token) {
      this.currentUserSubject.next(this.getStoredUser());
    } else {
      this.currentUserSubject.next(null);
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private getStoredUser(): User | null {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  get isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /** True when the signed-in user still needs to enter the email verification code. */
  needsEmailVerification(): boolean {
    const u = this.getCurrentUser();
    return this.isAuthenticated && u?.emailConfirmed === false;
  }

  refreshProfileFromServer(): Observable<UserProfile | null> {
    if (!this.isAuthenticated) {
      return of(null);
    }
    return this.api.getData<UserProfile>('auth/me', undefined, { suppressToast: true }).pipe(
      tap(profile => {
        this.updateCurrentUser(profileToUser(profile));
      }),
      catchError(() => of(null))
    );
  }

  verifyEmail(email: string, token: string): Observable<void> {
    return this.api.postData<void>('auth/verify-email', { email, token }, { suppressToast: true });
  }

  /** Verifies the OTP (same rules as verify-email) and returns a new session (access + refresh tokens). */
  verifyEmailAndSignIn(email: string, token: string): Observable<AuthResponse> {
    return this.api.postData<AuthResponse>('auth/verify-email/session', { email, token }, { suppressToast: true });
  }

  resendVerificationEmail(email: string): Observable<void> {
    return this.api.postData<void>('auth/resend-verification-email', { email }, { suppressToast: true });
  }

  forgotPassword(email: string): Observable<void> {
    return this.api.postData<void>('auth/forgot-password', { email }, { suppressToast: true });
  }

  resetPassword(email: string, token: string, newPassword: string): Observable<void> {
    return this.api.postData<void>('auth/reset-password', { email, token, newPassword }, { suppressToast: true });
  }

  login(credentials: { email: string; password: string }): Observable<AuthResponse> {
    // Login is the one endpoint where a 401 is *expected* (wrong credentials)
    // and the user is already on /login. Suppress both auto-redirect and toast
    // so the login form owns its own inline error display.
    return this.api.post<ApiResponse<AuthResponse>>('auth/login', credentials, {
      suppressToast: true,
      suppressAuthRedirect: true,
    }).pipe(
      map(res => {
        if (!res.success || res.data === undefined) {
          const msg = res.message || 'Request failed';
          throw new Error(msg);
        }
        return res.data;
      }),
      tap(res => {
        this.setSession(res.accessToken, profileToUser(res.user), { showWelcomeBanner: true });
      }),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          return throwError(() => err);
        }
        return throwError(() => err);
      })
    );
  }

  register(data: { firstName: string; lastName: string; email: string; password: string; subscribeToNewsletter?: boolean }): Observable<AuthResponse> {
    return this.api.postData<AuthResponse>('auth/register', data, { suppressToast: true });
  }

  socialLogin(body: SocialLoginRequest): Observable<SocialLoginResponse> {
    return this.api.postData<SocialLoginResponse>('auth/social-login', body, { suppressToast: true });
  }

  /** Store tokens and user after password login, register, attach-email, or social <c>authenticated</c> branch. */
  applyAuthenticatedResponse(res: AuthResponse, options?: { showWelcomeBanner?: boolean }): void {
    this.setSession(res.accessToken, profileToUser(res.user), options ?? { showWelcomeBanner: true });
  }

  attachSocialEmail(body: SocialAttachEmailRequest): Observable<AuthResponse> {
    return this.api.postData<AuthResponse>('auth/social-login/attach-email', body, { suppressToast: true }).pipe(
      tap(res => this.applyAuthenticatedResponse(res))
    );
  }

  private setSession(token: string, user: User, options?: { showWelcomeBanner?: boolean }): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
    if (options?.showWelcomeBanner) {
      sessionStorage.setItem(WELCOME_BANNER_SESSION_KEY, '1');
    }
  }

  shouldShowWelcomeBanner(): boolean {
    return sessionStorage.getItem(WELCOME_BANNER_SESSION_KEY) === '1';
  }

  dismissWelcomeBanner(): void {
    sessionStorage.removeItem(WELCOME_BANNER_SESSION_KEY);
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/']);
  }

  /** Clears stored credentials and current user without navigating. */
  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(WELCOME_BANNER_SESSION_KEY);
    this.currentUserSubject.next(null);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  updateCurrentUser(changes: Partial<User>): void {
    const current = this.currentUserSubject.value;
    if (!current) return;
    const updated = { ...current, ...changes };
    localStorage.setItem(USER_KEY, JSON.stringify(updated));
    this.currentUserSubject.next(updated);
  }

  get hasAnySubscription(): boolean {
    const u = this.currentUserSubject.value;
    return !!(u?.hasActiveReaderSubscription || u?.hasActiveWriterSubscription);
  }
}
